import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge knows only Tailwind's stock font sizes. Without this it reads
 * the custom `text-2xs` as a text COLOUR, and `cn('text-2xs text-muted-foreground')`
 * silently drops the size.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['2xs'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * A vertical scroll pane that does not clip its children's focus ring.
 *
 * THE BUG THIS EXISTS TO PREVENT. `overflow-y-auto` does not leave the other
 * axis alone: CSS computes `overflow-x: visible` to `auto` the moment its
 * partner is anything else, so a pane that only meant to scroll vertically
 * clips horizontally too. Our focus ring is `ring-2` on `ring-offset-2` — four
 * pixels painted OUTSIDE the control's border box — and a `w-full` input sits
 * flush against the pane's content edge. With no horizontal padding the left
 * four pixels of the ring are cut off, which is what a focused field in a
 * dialog looked like: a blue outline with a flat, shaved left edge.
 *
 * The negative margin is what makes it invisible. `px-1` alone would shove
 * every field four pixels right; `-mx-1 px-1` widens the pane by the same four
 * pixels it then pads, so content lands exactly where it did before and the
 * ring has room to paint. Compose it with the pane's own layout classes:
 *
 *     <div className={cn(SCROLL_PANE, 'flex-1 space-y-4')}>
 *
 * A pane that already has real padding on every side (`p-2` and up) needs
 * nothing — four pixels is the whole requirement.
 */
export const SCROLL_PANE = '-mx-1 overflow-y-auto px-1';

/**
 * The kit focus ring for a STANDALONE hand-built control (DESIGN_SYSTEM.md
 * §8.1) — the recipe ui/button, ui/input and ui/toggle carry. The offset paints
 * 2px of background plus a 2px ring OUTSIDE the border box (4px in total), so
 * it needs room: inside a scroll pane, give the pane SCROLL_PANE.
 *
 * `focus-visible:outline-none` lives inside the constant on purpose: a call
 * site can never drop the browser outline without also getting the ring.
 */
export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ring-offset-background';

/**
 * The same ring with NO offset, for rows packed in a rail, list, sidebar or
 * table header. An offset ring paints 4px outside the box, and the scroll pane
 * or sticky header around such rows would clip it — the failure SCROLL_PANE
 * exists to prevent. Never add an offset to a packed row.
 */
export const FOCUS_RING_ITEM = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/**
 * The section eyebrow (DESIGN_SYSTEM.md §3): 12px, medium, uppercase, wide
 * tracking, muted. The ONLY copy of the recipe — never retype the class string.
 * A dialog section's heading is `SectionHeading` (an h3 in this style); use the
 * bare constant for a label that is not a heading (a code well's caption).
 */
export const EYEBROW = 'text-xs font-medium uppercase tracking-wider text-muted-foreground';
